import React from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import {
  DashboardOutlined,
  EditOutlined,
  SettingsOutlined,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { motion } from "framer-motion";
import { SPORT_BY_ID } from "../../constants/sportWorkspace";
import { presentationName } from "../../utils/fanHubProfile";
import { brandRgba } from "../../utils/brandAssets";
import { fanHubPaperSx, sportAccent } from "./fanHubStyles";
import FanHubSportPills from "./FanHubSportPills";
import {
  FanHubAvatar,
  FanHubDisplayName,
  FanHubHandle,
} from "./FanHubIdentityMark";

const fanSince = (memberSince) => {
  if (!memberSince) return null;
  const year = new Date(memberSince).getFullYear();
  return Number.isFinite(year) ? `Fan since ${year}` : null;
};

const FanHubHero = ({ identity, onEdit, onPreferredSport }) => {
  const sport = identity?.preferredSport || "mlb";
  const accent = sportAccent(sport);
  const shown = presentationName(identity);
  const since = fanSince(identity?.memberSince);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
    >
      <Box
        sx={{
          ...fanHubPaperSx(accent),
          p: { xs: 2.5, sm: 3.5 },
          mb: 4,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            right: -48,
            top: -64,
            width: 220,
            height: 220,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${brandRgba(accent, 0.28)} 0%, transparent 70%)`,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            position: "relative",
          }}
        >
          <Box sx={{ display: "flex", gap: 2.5, minWidth: 0 }}>
            <FanHubAvatar
              src={identity?.avatar}
              name={shown}
              accent={accent}
              sport={sport}
              size={104}
            />
            <Box sx={{ minWidth: 0 }}>
              <FanHubHandle username={identity?.username} />
              <FanHubDisplayName>
                {shown || "Your fan profile"}
              </FanHubDisplayName>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1.25, maxWidth: 440 }}
              >
                Who you root for, who you follow, and the debates you keep
                coming back to.
              </Typography>
              {since && (
                <Typography
                  variant="caption"
                  sx={{ display: "block", mt: 1, color: accent }}
                >
                  {since}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Edit identity">
              <IconButton aria-label="Edit profile" onClick={onEdit} size="small">
                <EditOutlined />
              </IconButton>
            </Tooltip>
            <Tooltip title="Dashboard">
              <IconButton
                aria-label="Dashboard"
                component={RouterLink}
                to="/app/dashboard"
                size="small"
              >
                <DashboardOutlined />
              </IconButton>
            </Tooltip>
            <Tooltip title="Account settings">
              <IconButton
                aria-label="Settings"
                component={RouterLink}
                to="/app/settings"
                size="small"
              >
                <SettingsOutlined />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ mt: 3, position: "relative" }}>
          <FanHubSportPills
            value={sport}
            onChange={onPreferredSport}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            Preferred sport · {SPORT_BY_ID[sport]?.name}
          </Typography>
        </Box>
      </Box>
    </motion.div>
  );
};

export default FanHubHero;
